import { Chip as MuiChip, ChipProps as MuiChipProps, styled } from '@mui/material';
import { XIcon } from 'lucide-react';
import React from 'react';

import { colorSystem } from '../../theme';
import { Flexbox } from '../Flexbox';
import type { IconElement } from '../icons/IconElement';
import { iconSlot } from '../icons/iconSlot';
import { Text } from '../Text';

export interface ChipProps extends Omit<MuiChipProps, 'color'> {
    label: React.ReactNode;
    color: keyof typeof colorSystem;
    startIcon?: IconElement;
    showEllipsis?: boolean;
}

const StyledChip = styled(MuiChip)({
    height: 'fit-content',
    minHeight: 20,
    borderRadius: 24,
    '&.MuiChip-sizeSmall > .MuiChip-deleteIcon': {
        paddingRight: '4px',
        marginRight: '0px',
        marginLeft: '-2px',
    },
});

/** Sizes the start icon: call sites pass no size. */
const StartIcon = styled(Text)(iconSlot(16));

const ChipContent: React.FC<Pick<ChipProps, 'label' | 'startIcon' | 'showEllipsis' | 'disabled'>> = ({
    label,
    startIcon,
    showEllipsis,
    disabled,
}) => (
    <Flexbox
        gap={4}
        sx={{
            alignItems: 'center',
        }}
    >
        {startIcon && (
            <StartIcon
                style={{
                    fontSize: '16px',
                    lineHeight: '0px',
                    color: colorSystem.neutral[7],
                    marginLeft: '-2px',
                }}
            >
                {startIcon}
            </StartIcon>
        )}
        <Text
            variant="body-small"
            style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}
            color={disabled ? colorSystem.neutral[6] : colorSystem.neutral[9]}
            showEllipsis={showEllipsis}
        >
            {label}
        </Text>
    </Flexbox>
);

const getBackgroundColor = (color: ChipProps['color']) =>
    color === 'neutral' ? colorSystem.neutral[2] : colorSystem[color][3];

export const Chip = React.forwardRef<HTMLDivElement, ChipProps>(
    ({ label, color, startIcon, onDelete, showEllipsis, deleteIcon, disabled, ...muiChipProps }, ref) => {
        const backgroundColor = getBackgroundColor(color);

        return (
            <StyledChip
                ref={ref}
                label={
                    <ChipContent label={label} startIcon={startIcon} showEllipsis={showEllipsis} disabled={disabled} />
                }
                onDelete={onDelete}
                deleteIcon={onDelete ? <XIcon color={colorSystem.neutral[7]} /> : deleteIcon}
                disabled={disabled}
                {...muiChipProps}
                style={{
                    background: backgroundColor,
                    ...muiChipProps.style,
                }}
            />
        );
    },
);
